// src/pages/LiberarPedidoPage.js
import React, { useState } from "react";
import axios from "../axiosConfig";
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCcw,
  Search,
  ShieldCheck,
  Unlock,
  XCircle,
} from "lucide-react";

import "./LiberarPedidoPage.css";

function LiberarPedido() {
  const [numeroPedido, setNumeroPedido] = useState("");
  const [motivo, setMotivo] = useState("");
  const [resultados, setResultados] = useState([]);
  const [erro, setErro] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [liberando, setLiberando] = useState("");

  const buscarPedido = async () => {
    setErro("");
    setMensagem("");
    setResultados([]);

    const numero = numeroPedido.trim();

    if (!numero) {
      setErro("Informe o número do pedido.");
      return;
    }

    try {
      setBuscando(true);

      const { data } = await axios.get("/admin/liberar-pedido/buscar", {
        params: { numero_pedido: numero },
      });

      const dados = data?.dados || [];
      setResultados(dados);

      if (dados.length === 0) {
        setMensagem("Nenhum pedido encontrado com esse número.");
      }
    } catch (err) {
      setErro(err.response?.data?.error || "Erro ao buscar pedido.");
    } finally {
      setBuscando(false);
    }
  };

  const podeLiberar = (item) => {
    if (Number(item.recadastro_liberado) === 1) return false;

    const status = String(item.status || "").toLowerCase();
    const pagamento = String(item.status_pagamento || "").toLowerCase();

    if (item.modulo === "pontos") {
      return status === "rejeitado";
    }

    if (item.modulo === "pix") {
      return ["rejeitada", "nao_elegivel"].includes(status) && pagamento !== "pago";
    }

    return false;
  };

  const getTextoModulo = (item) => {
    return item.modulo === "pix" ? "Pix" : "Pontos";
  };

  const getTextoBotao = (item) => {
    return item.modulo === "pix" ? "Liberar Pix" : "Liberar Pontos";
  };

  const getMensagemConfirmacao = (item) => {
    const modulo = getTextoModulo(item).toUpperCase();

    if (item.modulo === "pix") {
      return (
        `Deseja liberar este pedido no módulo ${modulo}?\n\n` +
        "O histórico rejeitado será preservado.\n" +
        "O número original ficará disponível para novo cadastro no Pix Premiado."
      );
    }

    return (
      `Deseja liberar este pedido no módulo ${modulo}?\n\n` +
      "O histórico rejeitado será preservado.\n" +
      "O número original ficará disponível para novo cadastro de pontos."
    );
  };

  const liberarPedido = async (item) => {
    setErro("");
    setMensagem("");

    if (!motivo.trim()) {
      setErro("Informe o motivo da liberação antes de continuar.");
      return;
    }

    const confirmar = window.confirm(getMensagemConfirmacao(item));

    if (!confirmar) return;

    const chave = `${item.modulo}-${item.id}`;

    try {
      setLiberando(chave);

      const endpoint =
        item.modulo === "pix"
          ? `/admin/liberar-pedido/pix/${item.id}`
          : `/admin/liberar-pedido/pontos/${item.id}`;

      const { data } = await axios.put(endpoint, {
        motivo: motivo.trim(),
      });

      setMensagem(data?.message || `${getTextoBotao(item)} realizado com sucesso.`);
      await buscarPedido();
    } catch (err) {
      setErro(err.response?.data?.error || "Erro ao liberar pedido.");
    } finally {
      setLiberando("");
    }
  };

  const formatarData = (valor) => {
    if (!valor) return "-";

    try {
      return new Date(valor).toLocaleString("pt-BR");
    } catch {
      return "-";
    }
  };

  const formatarMoeda = (valor) => {
    return Number(valor || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const renderSituacaoLiberacao = (item) => {
    const liberado = Number(item.recadastro_liberado) === 1;

    if (liberado) {
      return (
        <div className="liberarPedido-situacaoBox">
          <span className="liberarPedido-liberado">
            <CheckCircle2 size={15} />
            Liberado
          </span>

          {item.recadastro_liberado_em && (
            <small>{formatarData(item.recadastro_liberado_em)}</small>
          )}

          {item.recadastro_liberado_motivo && (
            <small title={item.recadastro_liberado_motivo}>
              {item.recadastro_liberado_motivo}
            </small>
          )}
        </div>
      );
    }

    return (
      <span className="liberarPedido-naoLiberado">
        <XCircle size={15} />
        Bloqueado
      </span>
    );
  };

  return (
    <div className="liberarPedido-page">
      <div className="liberarPedido-container">
        <div className="liberarPedido-header">
          <div>
            <span className="liberarPedido-kicker">Administração</span>
            <h1>Liberação de Pedido</h1>
            <p>
              Libere números de pedidos rejeitados para novo cadastro sem apagar
              o histórico original da operação.
            </p>
          </div>

          <div className="liberarPedido-headerIcon">
            <Unlock size={28} />
          </div>
        </div>

        <div className="liberarPedido-infoBox">
          <ShieldCheck size={18} />
          <span>
            Esta rotina preserva a operação rejeitada, altera o número antigo
            para um identificador histórico e libera o número original somente
            no módulo escolhido: Pontos ou Pix.
          </span>
        </div>

        <div className="liberarPedido-card">
          <div className="liberarPedido-cardTop" />

          <div className="liberarPedido-cardBody">
            <div className="liberarPedido-sectionHeader">
              <h2>Buscar pedido</h2>
              <p>Informe o número exato usado no cadastro de pontos ou Pix.</p>
            </div>

            <div className="liberarPedido-searchGrid">
              <div className="liberarPedido-field">
                <label>Número do pedido</label>
                <input
                  type="text"
                  value={numeroPedido}
                  onChange={(e) => setNumeroPedido(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") buscarPedido();
                  }}
                  placeholder="Ex: 123456"
                />
              </div>

              <div className="liberarPedido-field">
                <label>Motivo da liberação</label>
                <input
                  type="text"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex: pedido lançado para cliente incorreto"
                />
              </div>

              <button
                type="button"
                className="liberarPedido-searchButton"
                onClick={buscarPedido}
                disabled={buscando}
              >
                {buscando ? (
                  <>
                    <RefreshCcw size={16} className="liberarPedido-spin" />
                    Buscando...
                  </>
                ) : (
                  <>
                    <Search size={16} />
                    Buscar
                  </>
                )}
              </button>
            </div>

            {erro && (
              <div className="liberarPedido-message erro">
                <AlertTriangle size={16} />
                {erro}
              </div>
            )}

            {mensagem && (
              <div className="liberarPedido-message sucesso">
                <CheckCircle2 size={16} />
                {mensagem}
              </div>
            )}
          </div>
        </div>

        <div className="liberarPedido-card">
          <div className="liberarPedido-cardTop" />

          <div className="liberarPedido-cardBody">
            <div className="liberarPedido-sectionHeader linha">
              <div>
                <h2>Resultado</h2>
                <p>{resultados.length} registro(s) encontrado(s).</p>
              </div>
            </div>

            {resultados.length === 0 ? (
              <div className="liberarPedido-empty">
                <Search size={34} />
                <strong>Nenhum registro carregado.</strong>
                <span>Faça uma busca pelo número do pedido.</span>
              </div>
            ) : (
              <div className="liberarPedido-tableWrap">
                <table className="liberarPedido-table">
                  <thead>
                    <tr>
                      <th>Módulo</th>
                      <th>Pedido</th>
                      <th>Cliente</th>
                      <th>Empresa</th>
                      <th>Status</th>
                      <th>Valor</th>
                      <th>Cadastro</th>
                      <th>Situação</th>
                      <th>Ação</th>
                    </tr>
                  </thead>

                  <tbody>
                    {resultados.map((item) => {
                      const chave = `${item.modulo}-${item.id}`;
                      const permitido = podeLiberar(item);
                      const carregando = liberando === chave;

                      return (
                        <tr key={chave}>
                          <td>
                            <span className={`liberarPedido-modulo ${item.modulo}`}>
                              {getTextoModulo(item)}
                            </span>
                          </td>

                          <td>
                            <strong>{item.numero_pedido}</strong>
                          </td>

                          <td>
                            <div className="liberarPedido-cliente">
                              <strong>{item.cliente_nome || "-"}</strong>
                              <span>{item.cliente_email || "-"}</span>
                            </div>
                          </td>

                          <td>{item.nome_empresa || "-"}</td>

                          <td>
                            <span
                              className={`liberarPedido-status ${String(
                                item.status || ""
                              ).toLowerCase()}`}
                            >
                              {item.status || "-"}
                            </span>
                          </td>

                          <td>{formatarMoeda(item.valor)}</td>
                          <td>{formatarData(item.data_cadastro)}</td>

                          <td>{renderSituacaoLiberacao(item)}</td>

                          <td>
                            <button
                              type="button"
                              className={`liberarPedido-releaseButton ${item.modulo}`}
                              disabled={!permitido || carregando}
                              onClick={() => liberarPedido(item)}
                              title={
                                permitido
                                  ? getTextoBotao(item)
                                  : "Este registro não pode ser liberado."
                              }
                            >
                              {carregando ? (
                                <>
                                  <RefreshCcw
                                    size={15}
                                    className="liberarPedido-spin"
                                  />
                                  Liberando...
                                </>
                              ) : (
                                <>
                                  <Unlock size={15} />
                                  {getTextoBotao(item)}
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LiberarPedido;